import { Component } from "react"

/* Components */
import { InnerLayout } from "../../components"

/* Modal */
import Modal from "react-modal"

/* Helpers */
import { utils } from "../../helpers"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { roomView, kick } from "../../api/Livekit"

/* Icons */
import { CloseIcon, UsersIcon, VideoIcon, EyeIcon, KickIcon, LockIcon } from "./StudioIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* HOW OFTEN THE PARTICIPANTS ARE READ AGAIN */
const INTERVAL = 15000


/* SHORT SIGN OF A PARTICIPANT */
const getInitials = (name) => {

    /* Check Name */
    if (!name || String(name).trim() === "") {
        return "?"
    }

    /* Send Result */
    return String(name).trim().charAt(0).toUpperCase()
}


/* Widget Studio Room */
class StudioRoom extends Component {

    constructor() {
        super()

        this.state = {
            visible: false,
            serviceURL: "",
            data: [],
            room: "",
            status: "loading",
        }

        this._timer = null
    }

    /* Open Action */
    open = ({ serviceURL, room }) => {

        this.setState({ visible: true, serviceURL, room }, () => {

            this.load()

            this._timer = setInterval(() => {
                this.load(false)
            }, INTERVAL)

        })
    }

    /* Close */
    close = () => {

        this.setState({ visible: false, serviceURL: "", room: "", status: "loading", data: [] })

        if (this._timer) {
            clearInterval(this._timer)
            this._timer = null
        }

    }

    componentWillUnmount = () => {

        /* Check Timer */
        if (this._timer) {
            clearInterval(this._timer)
            this._timer = null
        }
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { serviceURL, room } = this.state

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        roomView(serviceURL, { room }).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", data: response.data })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [] })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [] })
            }
            else {
                this.setState({ status: "error", data: [] })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [] })
        })
    }

    /* Kick */
    kick = (item) => {

        /* Fields */
        const { serviceURL, room } = this.state

        /* Ask */
        Swal.fire({
            title: t("studio.disableStreamTitle"),
            text: t("studio.disableStreamText"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            reverseButtons: true,
            confirmButtonText: t("studio.disableStreamButton"),
            cancelButtonText: t("swal.cancelButton"),
        }).then((result) => {
            if (result.isConfirmed) {

                kick(serviceURL, { room, identity: item.identity }).then((response) => {
                    if (response.status === 200) {
                        this.load(true)
                        Swal.fire(utils.notification(t("studio.disabledSuccess"), "success"))
                    }
                    else if (response.status === 401 || response.status === 403) {
                        Swal.fire(utils.notification(t("swal.noAccess"), "error"))
                    }
                    else if (response.status === 498) {
                        Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
                    }
                    else {
                        Swal.fire(utils.notification(t("swal.generic"), "error"))
                    }
                }).catch(() => {
                    Swal.fire(utils.notification(t("swal.generic"), "error"))
                })
            }
        })

    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data } = this.state
        const { admin } = this.props

        /* Rights — a view only admin never stops a stream */
        const canKick = Boolean(admin && admin.access !== "viewer")

        /* Layout */
        if (status !== "ok") {
            return <InnerLayout status={status} reload={() => this.load(true)} />
        }

        /* Check Data */
        if (data.length === 0) {
            return <div className="std--chips-empty">{t("studio.noParticipants")}</div>
        }

        return (
            <div className="std--room">

                {data.map((item, index) =>
                    <div className="std--room-row" key={`${index}`}>

                        <div className={`std--room-avatar ${item.isPublisher ? "publisher" : ""}`}>
                            {item.isPublisher ? <VideoIcon size={15} /> : getInitials(item.name)}
                        </div>

                        <div className="std--room-text">
                            <div className="std--room-name">{utils.checkValue(item.name, "—")}</div>
                            <div className="std--room-identity">{utils.checkValue(item.identity, "—")}</div>
                        </div>

                        <div className={`std--room-role ${item.isPublisher ? "publisher" : ""}`}>
                            {item.isPublisher ? <VideoIcon size={12} /> : <EyeIcon size={12} />}
                            <span>{item.isPublisher ? t("studio.publisher") : t("studio.viewer")}</span>
                        </div>

                        <div className="std--room-action">
                            {item.isPublisher &&
                                <div className="std--room-locked" title={t("studio.publisherLocked")}><LockIcon size={13} /></div>
                            }
                            {!item.isPublisher && canKick &&
                                <div onClick={() => this.kick(item)} className="std--room-kick" title={t("studio.disconnectStream")}>
                                    <KickIcon size={13} />
                                    <span>{t("studio.disconnectStream")}</span>
                                </div>
                            }
                        </div>

                    </div>
                )}

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, room, data, status } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="std--modal small"
                overlayClassName="std--overlay"
                closeTimeoutMS={200}
            >

                <div className="std--modal-header">

                    <div className="std--modal-icon">
                        <UsersIcon size={18} />
                    </div>

                    <div className="std--modal-head-text">
                        <h2>{t("studio.participantsTitle")}</h2>
                        <p>{room !== "" ? room : t("studio.participantsSubtitle")}</p>
                    </div>

                    {status === "ok" && <div className="std--counter big">{data.length}</div>}

                    <div onClick={() => this.close()} className="std--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                <div className="std--modal-body">
                    {this._content()}
                </div>

            </Modal>
        )
    }
}


const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(StudioRoom)
